import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import useCurrentLocation from '#/shared/hooks/useCurrentLocation';
import TripRequestForm from '#/trip-planner/components/TripRequestForm';
import Omnibox from '#/components/Omnibox';
import DetailViewLayout from '#/components/DetailViewLayout';
import DetailNavHeader from '#/shared/components/DetailNavHeader';
import SEO from '#/shared/components/SEO';
import useCurrentQuery from '#/trip-planner/hooks/useCurrentQuery';
import useNavigate from '#/shared/hooks/useNavigate';
import useGeolocation from '#/shared/hooks/useGeolocation';
import useRecentSearches from '#/shared/hooks/useRecentSearches';
import useFavoriteStops from '#/transit-tracker/hooks/useFavoriteStops';
import useViewMapControls from '#/map/hooks/useViewMapControls';
import { isOutsideBounds, positionToLocation } from '#/shared/utils/location';
import ROUTES from '#/constants/routes';
import { useGetNearbyStopsQuery } from '#/services/developerApi';
import {
  ORIGIN_LOCATION_TYPE,
  DESTINATION_LOCATION_TYPE,
  DEPARTURE_OPTION_SELECTOR,
  OTP_UI_CURRENT_LOCATION_TYPE,
} from '#/shared/constants';

function TripRequestContainer() {
  const { navigate, generateNavigateBack, generateNavigateClose, matchPath } =
    useNavigate();

  const { currentQuery, setCurrentQueryParam, clearCurrentQueryParam } =
    useCurrentQuery();

  const swapEndpoints = useCallback(() => {
    // We clone these objects to avoid a race condition
    const newOrigin = currentQuery.to ? { ...currentQuery.to } : null;
    const newDestination = currentQuery.from ? { ...currentQuery.from } : null;
    setCurrentQueryParam(DESTINATION_LOCATION_TYPE, newDestination);
    setCurrentQueryParam(ORIGIN_LOCATION_TYPE, newOrigin);
  }, [currentQuery, setCurrentQueryParam]);

  const { tripRequestViewOpened, simpleStopViewOpened, viewClosed } =
    useViewMapControls();

  useEffect(() => {
    tripRequestViewOpened();
    return viewClosed;
  }, [tripRequestViewOpened, viewClosed]);

  const planTrip = () => {
    navigate(ROUTES.trip);
  };
  const navigateToModeSelector = () => {
    navigate(ROUTES.plannerModeSettings);
  };
  const navigateToDateTimeSelector = () => {
    navigate(ROUTES.plannerTimeSettings);
  };

  const { currentLocation, currentLocationPosition } = useCurrentLocation();
  const [formLoaded, setFormLoaded] = useState(false);
  const { previousPathname } = useSelector(s => s.shared.history);
  const origin = currentQuery[ORIGIN_LOCATION_TYPE];
  const destination = currentQuery[DESTINATION_LOCATION_TYPE];
  const { geolocationApproved, isManualLocation } = useSelector(
    s => s.shared.location
  );

  useEffect(() => {
    // set the current location as the trip origin if the origin is null
    // and the user navigated from the app root or loaded the app here
    if (
      !formLoaded &&
      (!previousPathname || matchPath([ROUTES.home], previousPathname)) &&
      (geolocationApproved || isManualLocation) &&
      currentLocation
    ) {
      if (!origin) setCurrentQueryParam(ORIGIN_LOCATION_TYPE, currentLocation);
      setFormLoaded(true);
    }
  }, [
    currentLocation,
    formLoaded,
    geolocationApproved,
    isManualLocation,
    matchPath,
    origin,
    previousPathname,
    setCurrentQueryParam,
  ]);

  const [omniboxLocationType, setOmniboxLocationType] = useState(null);

  const {
    getCurrentPositionAsync,
    geolocationOutOfBounds,
    geolocationPaused,
    geolocationResumed,
  } = useGeolocation();

  const showOmnibox = useCallback(
    locationType => {
      setOmniboxLocationType(locationType);
      simpleStopViewOpened();
    },
    [simpleStopViewOpened]
  );

  const showOriginOmnibox = useCallback(
    () => showOmnibox(ORIGIN_LOCATION_TYPE),
    [showOmnibox]
  );

  const showDestinationOmnibox = useCallback(
    () => showOmnibox(DESTINATION_LOCATION_TYPE),
    [showOmnibox]
  );

  const hideOmnibox = useCallback(() => {
    setOmniboxLocationType(null);
    tripRequestViewOpened();
  }, [tripRequestViewOpened]);

  useEffect(() => {
    if (geolocationApproved) {
      if (omniboxLocationType) {
        // prevent the nearby stops list from being updated due to current
        // location changes from gps polling while the omnibox is visible
        geolocationPaused();
      } else {
        geolocationResumed();
      }
    }

    // ensure geolocation is resumed when component unmounts
    return () => geolocationApproved && geolocationResumed();
  }, [
    geolocationApproved,
    geolocationPaused,
    geolocationResumed,
    omniboxLocationType,
  ]);

  const navigateBack = omniboxLocationType
    ? hideOmnibox
    : generateNavigateBack();

  const navigateClose = omniboxLocationType ? null : generateNavigateClose();
  const geocoderConfig = useSelector(s => s.shared.location.geocoderConfig);

  const onLocationSelected = useCallback(
    ({ location, resultType }) => {
      let nextLocation = location;

      // the when the user selects "use current location" with otp-ui's
      // `location-field` and the user's location is known, it doesn't
      // return the reverse geocoded name, so use the current location
      // from the redux state, which has the name, as the query param
      if (resultType === OTP_UI_CURRENT_LOCATION_TYPE && currentLocation) {
        nextLocation = currentLocation;
      }
      setCurrentQueryParam(omniboxLocationType, nextLocation);
      hideOmnibox();
    },
    [currentLocation, hideOmnibox, omniboxLocationType, setCurrentQueryParam]
  );

  const { bounds } = useSelector(s => s.shared.location.locationConfig);
  const clearLocation = () =>
    omniboxLocationType && clearCurrentQueryParam(omniboxLocationType);

  const getCurrentPositionAndSetLocation = useCallback(async () => {
    const [position, label] = (await getCurrentPositionAsync(true)) || [];
    // if no position is returned geolocation was blocked or failed
    if (!position) {
      return;
    }

    const location = positionToLocation(position, label);

    if (isOutsideBounds(location, bounds)) {
      geolocationOutOfBounds();
      return;
    }

    onLocationSelected({
      omniboxLocationType,
      location,
    });
  }, [
    bounds,
    geolocationOutOfBounds,
    getCurrentPositionAsync,
    omniboxLocationType,
    onLocationSelected,
  ]);

  const { addRecentSearch: addLocationSearch, recentSearches } =
    useRecentSearches();

  const { maxWalkDistance } = currentQuery;
  const { favoriteStops, isFavoriteStop } = useFavoriteStops();
  const { data: nearbyStops } = useGetNearbyStopsQuery(
    {
      ll: `${currentLocation?.lat},${currentLocation?.lon}`,
      meters: maxWalkDistance,
    },
    { skip: !currentLocation?.lat || !currentLocation?.lon || !maxWalkDistance }
  );

  const filteredNearbyStops = useMemo(
    () =>
      nearbyStops
        ? [...nearbyStops]
            .filter(s => !isFavoriteStop(s))
            .sort((a, b) => a.metersDistance - b.metersDistance)
            .slice(0, 6)
        : [],
    [isFavoriteStop, nearbyStops]
  );

  const intl = useIntl();

  const selectStop = useCallback(
    (
      stop,
      { latKey = 'lat', lonKey = 'lon', nameKey = 'name', idKey = 'stopId' }
    ) => {
      if (stop) {
        onLocationSelected({
          omniboxLocationType,
          location: {
            lat: stop[latKey],
            lon: stop[lonKey],
            name: `${stop[nameKey]} (${intl.formatMessage(
              { id: 'planner.trimet-stop-id' },
              { id: stop[idKey] }
            )})`,
          },
        });
      }
    },
    [intl, omniboxLocationType, onLocationSelected]
  );

  const onNearbyStopSelected = useCallback(
    id => {
      selectStop(
        filteredNearbyStops.find(s => s.locid === id),
        { lonKey: 'lng', nameKey: 'desc', idKey: 'locid' }
      );
    },
    [filteredNearbyStops, selectStop]
  );

  const onFavoriteStopSelected = useCallback(
    stop =>
      selectStop(stop, {
        latKey: 'stopLat',
        lonKey: 'stopLng',
        nameKey: 'stopName',
      }),
    [selectStop]
  );

  const { mobileMode } = useSelector(s => s.user);

  const headerIntlIdLookup = {
    [ORIGIN_LOCATION_TYPE]: 'origin-title',
    [DESTINATION_LOCATION_TYPE]: 'destination-title',
    null: 'plan-title',
  };

  const { tripTimestamp } = useSelector(s => s.tripPlanner);

  return (
    <>
      <SEO metadataId="plannerForms" />
      <DetailViewLayout
        top={
          <DetailNavHeader
            onBack={navigateBack}
            onClose={navigateClose}
            content={
              <FormattedMessage
                id={`planner.${headerIntlIdLookup[omniboxLocationType]}`}
              />
            }
          />
        }
        bottom={
          !omniboxLocationType ? (
            <TripRequestForm
              origin={origin}
              destination={destination}
              currentLocation={currentLocation}
              planTrip={planTrip}
              departureOption={currentQuery[DEPARTURE_OPTION_SELECTOR]}
              tripTimestamp={tripTimestamp}
              swapEndpoints={swapEndpoints}
              onOriginFieldClick={showOriginOmnibox}
              onDestinationFieldClick={showDestinationOmnibox}
              onModeFieldClick={navigateToModeSelector}
              onDateTimeFieldClick={navigateToDateTimeSelector}
            />
          ) : (
            <Omnibox
              geocoderConfig={geocoderConfig}
              onLocationSelected={onLocationSelected}
              currentPosition={currentLocationPosition}
              getCurrentPosition={getCurrentPositionAndSetLocation}
              clearLocation={clearLocation}
              location={currentQuery[omniboxLocationType]}
              addLocationSearch={addLocationSearch}
              recentSearches={recentSearches}
              nearbyStops={filteredNearbyStops}
              favoriteStops={favoriteStops}
              onNearbyStopSelected={onNearbyStopSelected}
              onFavoriteStopSelected={onFavoriteStopSelected}
              locationType={omniboxLocationType}
              suppressNearby={!mobileMode && !isManualLocation}
            />
          )
        }
        hasInverseBackground
      />
    </>
  );
}

export default TripRequestContainer;
